import React, { useCallback, useState } from 'react';
import { Formik, Form } from 'formik';
import { signUpInitialValues as initialValues, signUpSchema as validationSchema, SignUpInfoCard } from '.';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import {
    callVerifyHuman,
    NETLIFY_FUNCTIONS_PATH,
    CTA_CLICK_SMS_OPT_IN,
    getFacebookCookies,
    getOptimizelyData,
    appendFormUrlToExposures,
    syncPardotTracking,
    isLeadsCaptureResponse,
    validateWithPossibleNow,
    getLeadsCaptureEcho,
    safeFullStory as FullStory,
} from '../../../utils';
import { navigate } from 'gatsby';
import axios from 'axios';
import Modal from 'react-bootstrap/Modal';
import {
    FormSuccess,
    FormFailed,
    FormSubmitting,
    AttributionSurvey,
    LocationsDropdownWrapper,
    MarketingConsentMsg,
} from '../shared';
import { useScrollToAnchor } from '../../../hooks/builder/useScrollToAnchor';
import { FormikControl } from '../../forms/formik';

export function SignUpForm({ siteData, showSignUpModal, setShowSignUpModal }) {
    const { executeRecaptcha } = useGoogleReCaptcha();
    const [showAttributionSurvey, setShowAttributionSurvey] = useState(false);
    // State to hold form values for use in attribution survey
    const [formValues, setFormValues] = useState(null);
    const { scrollToAnchor } = useScrollToAnchor({ offset: 300 });

    // Create an event handler so you can call the verification on button click event or form submit
    const handleReCaptchaVerify = useCallback(async () => {
        if (!executeRecaptcha) {
            console.log('Execute recaptcha not yet available');
            return;
        }
        const user = await callVerifyHuman('modalSignup', executeRecaptcha);
        return user;
        // Do whatever you want with the token
    }, [executeRecaptcha]);

    const handleSubmit = async (values, helpers) => {
        scrollToAnchor('signup-modal');
        helpers.setSubmitting(true);
        const { fbp, fbc } = getFacebookCookies();
        const { experiment_exposures, experiment_user_ID } = getOptimizelyData();
        try {
            const user = await handleReCaptchaVerify();
            if (user === 'bot') {
                helpers.setErrors('bot', true);
                throw new Error('Bot detected');
            } else {
                // Validate phone and email with PossibleNow
                const { phoneFound, wireless, emailFound, result, flags } = await validateWithPossibleNow(
                    values.phone,
                    values.email
                );

                const formData = {
                    ...values,
                    updates: true,
                    marketing_sms_optout: !CTA_CLICK_SMS_OPT_IN,
                    cloudamp__data__c: localStorage.getItem('cloudamp__data__c') || '',
                    formType: 'newsletter',
                    fbp,
                    fbc,
                    experiment_exposures: appendFormUrlToExposures(experiment_exposures),
                    experiment_user_ID,
                    phoneFound,
                    wireless,
                    emailFound,
                    result,
                    flags,
                };

                const response = await axios.post(`${NETLIFY_FUNCTIONS_PATH}form-handler`, formData);

                // Store form values for the attribution survey. The handler returns submissionId
                // and TU IDs when leads-capture is on; the survey echoes them so its answer
                // updates the original lead instead of creating a second one.
                setFormValues({ ...formData, ...getLeadsCaptureEcho(response) });

                // Only sync Pardot tracking on the legacy path; leads-capture already wrote the lead.
                if (response?.status === 200 && !isLeadsCaptureResponse(response)) {
                    syncPardotTracking(values);
                }

                // Show attribution survey instead of immediate success
                setShowAttributionSurvey(true);

                helpers.setSubmitting(false);
                helpers.setStatus('Success');
                // FullStory Event Tracking
                FullStory('trackEvent', {
                    name: 'Newsletter Form - Success',
                    properties: {
                        name: 'Newsletter',
                        user_email: values.email,
                        sms_optin: CTA_CLICK_SMS_OPT_IN,
                    },
                });

                navigate('?signup-success'); // This is for Marketing
                helpers.resetForm();
            }
        } catch (error) {
            console.error('Form submission error:', error);
            const errorMessage = error instanceof Error ? error.message : 'Form submission error';

            helpers.setSubmitting(false);
            helpers.setStatus('Failed');
            setFormValues(null);
            setShowAttributionSurvey(false);

            // FullStory Event Tracking with enhanced error details
            FullStory('trackEvent', {
                name: 'Newsletter Form - Error',
                properties: {
                    name: 'Newsletter',
                    user_email: values.email,
                    error_message: errorMessage,
                    error_details: axios.isAxiosError(error)
                        ? {
                              status: error.response?.status,
                              statusText: error.response?.statusText,
                          }
                        : 'Non-axios error',
                },
            });

            // Log that Pardot tracking was skipped due to form submission failure
            FullStory('trackEvent', {
                name: 'Newsletter Form - Pardot Sync Skipped',
                properties: {
                    user_email: values.email,
                    error_message: errorMessage,
                    reason: 'Actual Form submission failed',
                },
            });
        }
    };

    const handleAttributionComplete = () => {
        setShowAttributionSurvey(false);

        setTimeout(() => {
            setFormValues(null);
        }, 10000); // Clear form values after 10 second
    };

    return (
        <Modal
            centered
            id="signup-modal"
            show={showSignUpModal}
            onHide={() => setShowSignUpModal(false)}
            aria-labelledby="signup-modal-title"
            contentClassName="milan-bg-white"
            className={formValues && showAttributionSurvey ? 'modal-lg' : ''}
        >
            {!formValues && !showAttributionSurvey && (
                <Modal.Header className="border-bottom-0 py-0">
                    <Modal.Title id="signup-modal-title">
                        <h3 className="subhead-sm mb-0 mt-4 milan-text-primary">Get Milan Laser Updates</h3>
                    </Modal.Title>
                </Modal.Header>
            )}
            <Modal.Body className="mb-2">
                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                    validateOnChange={false}
                >
                    {(formik) => {
                        if (formik.isSubmitting) {
                            return <FormSubmitting />;
                        }

                        // Handle error state
                        if (formik.status === 'Failed') {
                            return <FormFailed />;
                        }

                        if (formValues) {
                            // At this point the form is submitted
                            if (showAttributionSurvey) {
                                return (
                                    <AttributionSurvey
                                        formValues={formValues}
                                        onComplete={handleAttributionComplete}
                                        InfoCard={SignUpInfoCard}
                                    />
                                );
                            }
                            return <FormSuccess />;
                        }
                        return (
                            <Form className="w-100 fs-mask">
                                <div className="row gy-2">
                                    <div>
                                        <div className="row mb-3 text-start gx-3">
                                            <div className="col-12 col-sm-6 mb-3 mb-sm-0">
                                                <FormikControl
                                                    control="input"
                                                    name="first_name"
                                                    placeholder="First Name"
                                                    type="text"
                                                    required
                                                    role="input"
                                                />
                                            </div>
                                            <div className="col-12 col-sm-6">
                                                <FormikControl
                                                    control="input"
                                                    name="last_name"
                                                    placeholder="Last Name"
                                                    type="text"
                                                    required
                                                    role="input"
                                                />
                                            </div>
                                        </div>
                                        <div className="mb-3 text-start">
                                            <FormikControl
                                                control="emailInput"
                                                name="email"
                                                placeholder="Your Email"
                                                required
                                                role="input"
                                            />
                                        </div>
                                        <div className="text-start">
                                            <FormikControl
                                                control="phoneInput"
                                                name="phone"
                                                placeholder="Phone Number"
                                                type="tel"
                                                required
                                                role="input"
                                            />
                                        </div>
                                    </div>
                                    <div id="locations-dropdown-wrapper">
                                        <label htmlFor="location">Location</label>
                                        <LocationsDropdownWrapper {...{ siteData, formik }} fieldName="location" />
                                    </div>
                                </div>

                                <div className="row justify-content-center my-3">
                                    <div className="col-12 lh-1">
                                        <MarketingConsentMsg
                                            cta={
                                                <>
                                                    Submit <sup>*</sup>
                                                </>
                                            }
                                        />
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-12 text-center">
                                        <button
                                            className="milan-btn milan-red-btn w-100"
                                            type="submit"
                                            disabled={!formik.isValid || formik.isSubmitting}
                                        >
                                            {formik.isSubmitting ? (
                                                'Submitting...'
                                            ) : (
                                                <>
                                                    Submit <sup>*</sup>
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </Form>
                        );
                    }}
                </Formik>
            </Modal.Body>
        </Modal>
    );
}

export default SignUpForm;
